import { mergeAttributes, Node } from '@tiptap/core';
import { IFormInputContext, IFormInputType } from 'pages/revisions-module/template-editor/editors/form/types';
import { INPUT_TYPE_NAMES } from 'pages/revisions-module/template-editor/editors/form/configurator/InputTypeSelect';
import getJsonAttribute from 'utils/get-json-attribute';

export type IPlaceholderType =
  | IFormInputType
  | 'technicianStamp'
  | 'technicianSignature'
  | 'technicianCertificateRenewalDate'
  | 'technicianProperty'
  | 'approverProperty'
  | 'confirmerProperty'
  | 'revisionProperty'
  | 'organizationLogo'
  | 'organizationProperty'
  | 'technicianOrganizationLogo'
  | 'technicianOrganizationProperty'
  | 'assignerOrganizationLogo'
  | 'assignerOrganizationProperty'
  | 'documentProperty'
  | 'deviceFaultsOrderedList'
  | 'confirmationProperty'
  | 'confirmationSignature';

export type IPlaceholderContext = IFormInputContext | 'global';

export interface IPlaceholder {
  context: IPlaceholderContext;
  name: string;
  label: string;
  type: IPlaceholderType;
  format?: string;
}

export interface ISpecialPlaceholder extends IPlaceholder {
  name: `_${string}`;
}

export interface PlaceholderOptions {
  inline: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    placeholder: {
      insertPlaceholder: (placeholder: IPlaceholder) => ReturnType;
      setPlaceholderFormat: (format: any) => ReturnType;
    };
  }
}

/** Returns the label of the placeholder type. */
export function getPlaceholderTypeLabel(type: IPlaceholderType) {
  switch (type) {
    case 'technicianProperty':
    case 'technicianStamp':
    case 'technicianSignature':
    case 'technicianCertificateRenewalDate':
      return 'Vlastnosť revízneho technika';

    case 'approverProperty':
      return 'Vlastnosť schvaľovateľa';

    case 'confirmerProperty':
      return 'Vlastnosť preberateľa';

    case 'revisionProperty':
      return 'Vlastnosť revíznej správy';

    case 'organizationProperty':
    case 'organizationLogo':
      return 'Vlastnosť organizácie';

    case 'technicianOrganizationProperty':
    case 'technicianOrganizationLogo':
      return 'Vlastnosť organizácie RT';

    case 'assignerOrganizationProperty':
    case 'assignerOrganizationLogo':
      return 'Vlastnosť organizácie zadávateľa';

    case 'documentProperty':
      return 'Vlastnosť dokumentu';

    case 'deviceFaultsOrderedList':
      return 'Nedostatky zariadenia';

    case 'confirmationProperty':
    case 'confirmationSignature':
      return 'Vlastnosť potvrdenia';

    default:
      return INPUT_TYPE_NAMES[type]?.label ?? '';
  }
}

/** Returns the kind of the placeholder. */
export function getPlaceholderKind(type: IPlaceholderType, name: string): 'inline' | 'block' {
  const blockNames: string[] = ['_departmentLevels'];

  const blockTypes: IPlaceholderType[] = [
    'measuringDevice',
    'predefinedList',
    'wysiwyg',
    'deviceFaultsOrderedList',
    'technicianStamp',
    'technicianSignature',
    'organizationLogo',
    'technicianOrganizationLogo',
    'assignerOrganizationLogo',
    'confirmationSignature',
    'fileList',
  ];

  const isBlock = blockNames.includes(name) || blockTypes.includes(type);

  return isBlock ? 'block' : 'inline';
}

/** Returns the path suffix based on the placeholder type. */
export function getPlaceholderPathSuffix(type: IPlaceholderType) {
  switch (type) {
    case 'safetyPoint':
      return 'label';

    case 'predefinedSelect':
      return 'primary';

    case 'qualification':
      return 'qualificationNumber';

    default:
      return '';
  }
}

/** Determines whether the placeholder is configurable. */
export function isPlaceholderConfigurable(type: IPlaceholderType): boolean {
  const configurableTypes: IPlaceholderType[] = [
    'predefinedList',
    'technicianStamp',
    'organizationLogo',
    'technicianOrganizationLogo',
    'assignerOrganizationLogo',
    'confirmationSignature',
    'technicianSignature',
    'fileList',
  ];

  return configurableTypes.includes(type);
}

export const DEVICE_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  { context: 'device', name: '_deviceName', label: 'Názov zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceLocation', label: 'Umiestnenie zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceManufacturer', label: 'Výrobca zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceManufactured', label: 'Rok výroby zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceSerialNumber', label: 'Výrobné číslo zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceTypeName', label: 'Skupina zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceSubTypeName', label: 'Typ zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceBuilding', label: 'Budova zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceFloor', label: 'Podlažie zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceRoom', label: 'Miestnosť zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceLatitude', label: 'Zemepisná šírka zariadenia (GPS)', type: 'deviceProperty' },
  { context: 'device', name: '_deviceLongitude', label: 'Zemepisná dĺžka zariadenia (GPS)', type: 'deviceProperty' },
  {
    context: 'device',
    name: '_deviceInternalPossessionsNumber',
    label: 'Interné číslo zariadenia',
    type: 'deviceProperty',
  },
  { context: 'device', name: '_deviceInternalNote', label: 'Interná poznámka zariadenia', type: 'deviceProperty' },
  { context: 'device', name: '_deviceFaultCount', label: 'Počet nedostatkov zariadenia', type: 'deviceProperty' },
  {
    context: 'device',
    name: '_deviceFaultAttachmentCount',
    label: 'Počet príloh nedostatkov zariadenia',
    type: 'deviceProperty',
  },
  { context: 'device', name: '_isParentDevice', label: 'Zariadenie je nadradené', type: 'deviceProperty' },
  { context: 'device', name: '_isChildDevice', label: 'Zariadenie je podradené', type: 'deviceProperty' },
];

export const TECHNICIAN_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  { context: 'revision', name: '_technicianName', label: 'Meno revízneho technika', type: 'technicianProperty' },
  {
    context: 'revision',
    name: '_technicianOrganization',
    label: 'Organizácia revízneho technika',
    type: 'technicianProperty',
  },
  {
    context: 'revision',
    name: '_technicianPhone',
    label: 'Telefónne číslo revízneho technika',
    type: 'technicianProperty',
  },
  { context: 'revision', name: '_technicianEmail', label: 'Email revízneho technika', type: 'technicianProperty' },
  {
    context: 'revision',
    name: '_technicianCertificateNumber',
    label: 'Číslo osvedčenia revízneho technika',
    type: 'technicianProperty',
  },
  {
    context: 'revision',
    name: '_technicianCertificateStamp',
    label: 'Pečiatka revízneho technika',
    type: 'technicianStamp',
  },
  {
    context: 'revision',
    name: '_technicianCertificateRenewalDate',
    label: 'Aktualizačná príprava revízneho technika',
    type: 'technicianCertificateRenewalDate',
  },
  {
    context: 'revision',
    name: '_technicianSignatureFileId',
    label: 'Podpis vypracovateľa',
    type: 'technicianSignature',
  },
];

export const APPROVER_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  { context: 'revision', name: '_approverName', label: 'Meno schvaľovateľa', type: 'approverProperty' },
  { context: 'revision', name: '_approverOrganization', label: 'Organizácia schvaľovateľa', type: 'approverProperty' },
  { context: 'revision', name: '_approverPhone', label: 'Telefónne číslo schvaľovateľa', type: 'approverProperty' },
  { context: 'revision', name: '_approverEmail', label: 'Email schvaľovateľa', type: 'approverProperty' },
  { context: 'revision', name: '_approverDate', label: 'Dátum schválenia', type: 'approverProperty' },
  { context: 'revision', name: '_approverTime', label: 'Čas schválenia', type: 'approverProperty' },
];

export const CONFIRMER_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  { context: 'revision', name: '_confirmerName', label: 'Meno preberateľa', type: 'confirmerProperty' },
  { context: 'revision', name: '_confirmerOrganization', label: 'Organizácia preberateľa', type: 'confirmerProperty' },
  { context: 'revision', name: '_confirmerPhone', label: 'Telefónne číslo preberateľa', type: 'confirmerProperty' },
  { context: 'revision', name: '_confirmerEmail', label: 'Email preberateľa', type: 'confirmerProperty' },
  { context: 'revision', name: '_confirmerDate', label: 'Dátum prebratia', type: 'confirmerProperty' },
  { context: 'revision', name: '_confirmerTime', label: 'Čas prebratia', type: 'confirmerProperty' },
];

export const REVISION_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  { context: 'revision', name: '_revisionId', label: 'ID revíznej správy', type: 'revisionProperty' },
  { context: 'revision', name: '_revisionURL', label: 'URL revíznej správy', type: 'revisionProperty' },
  { context: 'revision', name: '_revisionName', label: 'Názov revíznej správy', type: 'revisionProperty' },
  { context: 'revision', name: '_organization', label: 'Organizácia', type: 'revisionProperty' },
  { context: 'revision', name: '_department', label: 'Položka úrovne', type: 'revisionProperty' },
  { context: 'revision', name: '_departmentPath', label: 'Celá cesta položky úrovne', type: 'revisionProperty' },
  { context: 'revision', name: '_departmentLevels', label: 'Rozpis úrovní', type: 'revisionProperty' },
  { context: 'revision', name: '_deviceCount', label: 'Počet zariadení', type: 'revisionProperty' },
  { context: 'revision', name: '_totalFaultCount', label: 'Počet nedostatkov celkovo', type: 'revisionProperty' },
  {
    context: 'revision',
    name: '_totalFaultAttachmentCount',
    label: 'Počet príloh nedostatkov celkovo',
    type: 'revisionProperty',
  },
];

export const ORGANIZATION_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  { context: 'revision', name: '_organizationName', label: 'Názov organizácie', type: 'organizationProperty' },
  {
    context: 'revision',
    name: '_organizationOfficialName',
    label: 'Oficiálny názov organizácie',
    type: 'organizationProperty',
  },
  { context: 'revision', name: '_organizationLogo', label: 'Logo organizácie', type: 'organizationLogo' },
  { context: 'revision', name: '_organizationIco', label: 'IČO organizácie', type: 'organizationProperty' },
  { context: 'revision', name: '_organizationDic', label: 'DIČ organizácie', type: 'organizationProperty' },
  { context: 'revision', name: '_organizationIcdph', label: 'IČ DPH organizácie', type: 'organizationProperty' },
];

export const TECHNICIAN_ORGANIZATION_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  {
    context: 'revision',
    name: '_technicianOrganizationOfficialName',
    label: 'Oficiálny názov organizácie RT',
    type: 'technicianOrganizationProperty',
  },
  {
    context: 'revision',
    name: '_technicianOrganizationName',
    label: 'Názov organizácie RT',
    type: 'technicianOrganizationProperty',
  },
  {
    context: 'revision',
    name: '_technicianOrganizationLogo',
    label: 'Logo organizácie RT',
    type: 'technicianOrganizationLogo',
  },
  {
    context: 'revision',
    name: '_technicianOrganizationIco',
    label: 'IČO organizácie RT',
    type: 'technicianOrganizationProperty',
  },
  {
    context: 'revision',
    name: '_technicianOrganizationDic',
    label: 'DIČ organizácie RT',
    type: 'technicianOrganizationProperty',
  },
  {
    context: 'revision',
    name: '_technicianOrganizationIcdph',
    label: 'IČ DPH organizácie RT',
    type: 'technicianOrganizationProperty',
  },
];

export const ASSIGNER_ORGANIZATION_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  {
    context: 'revision',
    name: '_assignerOrganizationOfficialName',
    label: 'Oficiálny názov organizácie zadávateľa',
    type: 'assignerOrganizationProperty',
  },
  {
    context: 'revision',
    name: '_assignerOrganizationName',
    label: 'Názov organizácie zadávateľa',
    type: 'assignerOrganizationProperty',
  },
  {
    context: 'revision',
    name: '_assignerOrganizationLogo',
    label: 'Logo organizácie zadávateľa',
    type: 'assignerOrganizationLogo',
  },
  {
    context: 'revision',
    name: '_assignerOrganizationIco',
    label: 'IČO organizácie zadávateľa',
    type: 'assignerOrganizationProperty',
  },
  {
    context: 'revision',
    name: '_assignerOrganizationDic',
    label: 'DIČ organizácie zadávateľa',
    type: 'assignerOrganizationProperty',
  },
  {
    context: 'revision',
    name: '_assignerOrganizationIcdph',
    label: 'IČ DPH organizácie zadávateľa',
    type: 'assignerOrganizationProperty',
  },
];

export const DEPARTMENT_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [];

export const DOCUMENT_PROPERTY_PLACEHOLDERS: ISpecialPlaceholder[] = [
  { context: 'global', name: '_page', label: 'Číslo strany', type: 'documentProperty' },
  { context: 'global', name: '_pages', label: 'Počet strán', type: 'documentProperty' },
  {
    context: 'global',
    name: '_pagesWordParadigm',
    label: 'Vyskloňované strana / strany / strán',
    type: 'documentProperty',
  },
];

export const SPECIAL_PLACEHOLDERS: ISpecialPlaceholder[] = [
  ...DEVICE_PROPERTY_PLACEHOLDERS,
  ...TECHNICIAN_PROPERTY_PLACEHOLDERS,
  ...APPROVER_PROPERTY_PLACEHOLDERS,
  ...CONFIRMER_PROPERTY_PLACEHOLDERS,
  ...REVISION_PROPERTY_PLACEHOLDERS,
  ...ORGANIZATION_PROPERTY_PLACEHOLDERS,
  ...TECHNICIAN_ORGANIZATION_PROPERTY_PLACEHOLDERS,
  ...ASSIGNER_ORGANIZATION_PROPERTY_PLACEHOLDERS,
  ...DEPARTMENT_PROPERTY_PLACEHOLDERS,
  ...DOCUMENT_PROPERTY_PLACEHOLDERS,
];

const Placeholder = Node.create<PlaceholderOptions>({
  name: 'placeholder',
  draggable: true,

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  addOptions() {
    return {
      inline: true,
      HTMLAttributes: {
        class: 'placeholder',
      },
    };
  },

  addAttributes() {
    return {
      'data-name': { default: '' },
      'data-type': { default: '' },
      'data-format': { default: '' },
      'data-context': {
        default: '',
        parseHTML: (el) => {
          const context = el.getAttribute('data-context');

          if (context === 'fields') {
            return 'revision'; // Backward compatibility
          }

          return context;
        },
      },
      'data-path': {
        default: '',
        renderHTML: (attributes) => {
          const name = attributes['data-name'];
          const context = attributes['data-context'];
          const type = attributes['data-type'];

          const prefix = context === 'global' ? 'global' : 'fields';
          const suffix = getPlaceholderPathSuffix(type);

          const path = [prefix, name, suffix].filter(Boolean).join('.');

          return {
            'data-path': path,
          };
        },
      },
      'data-inject-field-name': {
        default: '',
        renderHTML: (attributes) => ({ 'data-inject-field-name': attributes['data-name'] }),
      },
      format: {
        renderHTML: (attributes) => ({ 'data-format': JSON.stringify(attributes.format) }),
        parseHTML: (element) => getJsonAttribute(element, 'data-format', {}),
        default: {},
      },
    };
  },

  parseHTML() {
    const tag = this.options.inline ? 'span' : 'div';
    return [{ tag: `${tag}.placeholder`, priority: 50 }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const type = node.attrs['data-type'];
    const tag = this.options.inline ? 'span' : 'div';

    const ePlaceholder = document.createElement(tag);
    const attributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes);

    for (const [key, value] of Object.entries(attributes)) {
      ePlaceholder.setAttribute(key, value);
    }

    if (isPlaceholderConfigurable(type)) {
      ePlaceholder.style.cursor = 'pointer';
      ePlaceholder.title = 'Dvojklikom otvoríte konfigurátor';
      ePlaceholder.classList.add('configurable');

      ePlaceholder.ondblclick = () =>
        window.dispatchEvent(new CustomEvent('open-placeholder-configurator', { detail: { type } }));
    }

    return ePlaceholder;
  },

  addCommands() {
    return {
      insertPlaceholder:
        ({ name, context, type }) =>
        ({ commands }) => {
          const kind = getPlaceholderKind(type, name);

          return commands.insertContent({
            type: `${kind}Placeholder`,
            attrs: {
              'data-name': name,
              'data-type': type,
              'data-context': context,
            },
          });
        },
      setPlaceholderFormat:
        (format: any) =>
        ({ commands }) => {
          return commands.updateAttributes('blockPlaceholder', { format });
        },
    };
  },
});

export const InlinePlaceholder = Placeholder.extend({ name: 'inlinePlaceholder' }).configure({ inline: true });
export const BlockPlaceholder = Placeholder.extend({ name: 'blockPlaceholder' }).configure({ inline: false });
